import clsx from "clsx";
import { ArrowLeft, Check, FileText, FileUp, Loader2, ScanSearch, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import type { Retex, RetexAnalysis, RetexCustomEnemy, RetexEnemy } from "../../api/types";
import { designate } from "../../components/attention";
import { Callout } from "../../components/Callout";
import { Checkbox } from "../../components/Checkbox";
import { Dialog, DialogBody } from "../../components/Dialog";
import { motion, Presence } from "../../components/presence";
import { Button, IconButton, Segmented, textareaClass } from "../../components/ui";
import { locale, t, tServer } from "../../i18n/i18n";
import { plural } from "../../plural";
import { useUi } from "../../state/ui";
import { useUnsavedDraft } from "../../state/unsavedDrafts";
import { ACCEPTED, fileSize, MAX_BYTES } from "../received/ImportDialog";
import { Waiting } from "../../components/Waiting";

/** Import d'un compte rendu au RETEX (scanner RETEX de l'iPad, sans caméra) : le document est lu et découpé en
 *  passages, chacun classé par ennemi ; les passages au seuil d'un mode d'action ennemi
 *  (score 6) sont cochés, l'utilisateur choisit l'ennemi et ce qu'il verse. Rien n'est gardé avant « Verser ». */

export const ENEMIES: RetexEnemy[] = ["Titan", "Titane", "Mercure", "Tantale", "Deimos"];
/** « ENI TITAN » ; l'ENI personnalisé du dossier par son nom de code (registre A135), jamais par sa clé. */
export const eniLabel = (eni: RetexEnemy, custom?: RetexCustomEnemy | null) =>
  t("ENI {name}", { name: custom && eni === custom.key ? custom.code : eni.toUpperCase() });
/** Ennemis proposés au versement : l'ENI personnalisé du dossier d'abord, puis les cinq ennemis de référence. */
export const retexEnemies = (custom?: RetexCustomEnemy | null): RetexEnemy[] => (custom ? [custom.key, ...ENEMIES] : ENEMIES);

function PassageRow({
  text,
  score,
  threshold,
  checked,
  onToggle,
}: {
  text: string;
  score: number;
  threshold: number;
  checked: boolean;
  onToggle: () => void;
}) {
  const [open, setOpen] = useState(false);
  const long = text.length > 320;
  const strong = score >= threshold;
  return (
    <li className={clsx("flex gap-3 px-4 py-3 transition-colors", checked ? "bg-accent-soft" : "hover:bg-hover")}>
      <Checkbox
        className="mt-0.5"
        checked={checked}
        onChange={onToggle}
        title={checked ? t("Ne pas verser ce passage") : t("Verser ce passage")}
        aria-label={checked ? t("Ne pas verser ce passage") : t("Verser ce passage")}
      />
      <div className="min-w-0 flex-1">
        <p className={clsx("text-left text-ui leading-relaxed whitespace-pre-line text-ink hyphens-auto sm:text-justify", !open && long && "line-clamp-4")}>
          {text}
        </p>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-meta text-ink-muted">
          <span className={clsx("font-medium tabular-nums", strong ? "text-ok" : "text-ink-muted")}>
            {strong ? t("Mode d'action ennemi") : t("Peu caractéristique")} · {t("score {score}", { score: score.toLocaleString(locale()) })}
          </span>
          <span className="tabular-nums">{plural(text.length, t("caractère"), t("caractères"))}</span>
          {long && (
            <Button variant="link" onClick={() => setOpen((o) => !o)}>
              {open ? t("Réduire") : t("Lire tout")}
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}

export function RetexImportDialog({
  retex,
  initialFile,
  onAnalyze,
  onAdd,
  onClose,
}: {
  retex: Retex;
  initialFile?: File | null;
  onAnalyze: (input: { file: File | null; text: string }) => Promise<RetexAnalysis>;
  onAdd: (eni: RetexEnemy, passages: string[], source: string) => Promise<void>;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<"file" | "text">("file");
  const [file, setFile] = useState<File | null>(initialFile ?? null);
  const [text, setText] = useState("");
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<RetexAnalysis | null>(null);
  const custom = retex.custom;
  const label = (e: RetexEnemy) => eniLabel(e, custom);
  const [eni, setEni] = useState<RetexEnemy>(custom?.key ?? "Mercure");
  const [chosen, setChosen] = useState<Set<number>>(new Set());
  const input = useRef<HTMLInputElement>(null);
  const target = useRef<HTMLElement | null>(null);
  const passagesRef = useRef<HTMLUListElement>(null);
  const notify = useUi((s) => s.notify);
  const tooBig = Boolean(file && file.size > MAX_BYTES);
  const ready = mode === "file" ? Boolean(file) && !tooBig : text.trim().length >= 40;

  // Compte rendu choisi, collé ou déjà analysé : un clic à côté ou Échap demandent avant de fermer, la fermeture de
  // l'onglet aussi (audit de persistance M4).
  const dirty = Boolean(text.trim() || file || analysis);
  useUnsavedDraft("retex:import", dirty, { label: t("Compte rendu à verser au RETEX"), discard: onClose });

  // Ennemi changé : les passages cochés sont ceux qui atteignent le seuil pour lui.
  const suggest = (a: RetexAnalysis, e: RetexEnemy) =>
    new Set(a.passages.flatMap((p, i) => ((p.scores[e] ?? 0) >= a.threshold ? [i] : [])));
  useEffect(() => {
    if (analysis) setChosen(suggest(analysis, eni));
  }, [analysis, eni]);

  const group = retex.enemies.find((g) => g.eni === eni);
  const after = (group?.count ?? 0) + chosen.size;
  const dropped = Math.max(0, after - retex.limits.entries);
  const selected = useMemo(() => analysis?.passages.filter((_, i) => chosen.has(i)) ?? [], [analysis, chosen]);

  const analyze = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await onAnalyze({ file: mode === "file" ? file : null, text });
      setAnalysis(result);
      setEni(result.default_eni);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const add = async () => {
    if (!analysis) return;
    setBusy(true);
    setError(null);
    try {
      await onAdd(
        eni,
        selected.map((p) => p.text),
        analysis.source,
      );
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const doc = analysis?.document;
  return (
    <Dialog
      title={t("Importer un compte rendu au RETEX")}
      subtitle={
        analysis
          ? t("Choisissez l'ennemi et les passages à verser ; les autres ne sont pas gardés.")
          : t("Le compte rendu est lu, découpé en passages et classé par ennemi ; rien n'est versé sans votre choix.")
      }
      width={analysis ? 680 : 580}
      onClose={onClose}
      dirty={dirty}
      footer={
        analysis ? (
          <>
            <Button variant="ghost" onClick={() => setAnalysis(null)} disabled={busy} className="mr-auto">
              <ArrowLeft size={14} /> {t("Autre document")}
            </Button>
            <Button variant="secondary" onClick={onClose}>
              {t("Annuler")}
            </Button>
            <Button
              onClick={() => void add()}
              disabled={busy}
              blocked={selected.length === 0}
              onBlocked={() => {
                // Aucun passage coché : la liste des passages est l'action attendue (R12).
                designate(passagesRef.current, "ring");
                notify({ text: t("Cochez au moins un passage à verser."), tone: "info" });
              }}
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {selected.length > 1 ? t("Verser {n} passages", { n: selected.length }) : t("Verser au RETEX")}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              {t("Annuler")}
            </Button>
            <Button
              disabled={busy}
              blocked={!ready}
              onBlocked={() => {
                // Préalable manquant : le fichier (ou le texte collé) est l'action attendue (R12).
                designate(target.current, mode === "text" ? "field" : "ring");
                notify({
                  text:
                    mode === "text"
                      ? t("Collez le texte du compte rendu (40 caractères au moins).")
                      : tooBig
                        ? t("Fichier trop volumineux : 15 Mo au plus.")
                        : t("Choisissez d'abord le fichier du compte rendu."),
                  tone: "info",
                });
              }}
              onClick={() => void analyze()}
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <ScanSearch size={14} />}
              {busy ? <Waiting>{t("Lecture du compte rendu…")}</Waiting> : t("Lire et classer")}
            </Button>
          </>
        )
      }
    >
      {!analysis ? (
        <DialogBody className="space-y-4">
          <Segmented
            label={t("Source du compte rendu")}
            value={mode}
            onChange={setMode}
            options={[
              { value: "file", label: t("Fichier") },
              { value: "text", label: t("Texte collé") },
            ]}
          />
          {mode === "file" ? (
            file ? (
              <div
                ref={(el) => {
                  target.current = el;
                }}
                className={clsx("flex items-center gap-3 rounded-xl border border-line bg-canvas/50 px-3.5 py-3", motion("fade"))}
              >
                <FileText size={20} className="shrink-0 text-ink-soft" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-ui font-medium text-ink">{file.name}</p>
                  <p className={clsx("text-meta", tooBig ? "text-danger" : "text-ink-muted")}>
                    {fileSize(file.size)}
                    {tooBig && ` — ${t("trop volumineux : 15 Mo au plus")}`}
                  </p>
                </div>
                <IconButton size="sm" onClick={() => setFile(null)} title={t("Choisir un autre fichier")} aria-label={t("Choisir un autre fichier")}>
                  <X size={16} />
                </IconButton>
              </div>
            ) : (
              <button
                ref={(el) => {
                  target.current = el;
                }}
                type="button"
                onClick={() => input.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setOver(true);
                }}
                onDragLeave={() => setOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setOver(false);
                  const given = e.dataTransfer.files[0];
                  if (given) setFile(given);
                }}
                className={clsx(
                  "flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors",
                  over ? "border-accent bg-accent-soft" : "border-line-strong hover:border-accent hover:bg-hover",
                )}
              >
                <FileUp size={24} className="text-ink-soft" />
                <span className="text-ui font-medium text-ink">
                  {t("Déposez le compte rendu, ou cliquez pour le choisir")}
                </span>
                <span className="text-meta leading-snug text-ink-muted">
                  {t("PDF, Word (.docx ou .doc), LibreOffice (.odt), RTF ou texte — 15 Mo au plus.")}
                  <br />
                  {t("Photos et PDF numérisés : la lecture des images n'est pas disponible, collez plutôt le texte.")}
                </span>
              </button>
            )
          ) : (
            <textarea
              ref={(el) => {
                target.current = el;
              }}
              value={text}
              autoFocus
              onChange={(e) => setText(e.target.value)}
              placeholder={t("Collez ici le compte rendu : observations sur l'ennemi, ses matériels, ses modes d'action…")}
              className={clsx(textareaClass, "min-h-[220px]")}
            />
          )}
          <Presence show={Boolean(error)}>{error && <Callout tone="danger">{error}</Callout>}</Presence>
          <input
            ref={input}
            type="file"
            accept={ACCEPTED}
            className="hidden"
            onChange={(e) => {
              const picked = e.target.files?.[0];
              if (picked) setFile(picked);
            }}
          />
        </DialogBody>
      ) : (
        <div className={motion("fade")}>
          <div className="space-y-3 border-b border-line px-4 py-3.5">
            <p className="text-ui leading-relaxed text-ink-soft">
              <span className="font-medium text-ink">{tServer(analysis.source)}</span>
              {" — "}
              {doc?.eni && doc.retained ? (
                <>
                  {t("reconnu comme un compte rendu sur l'")}
                  <span className="font-semibold text-ink">{label(doc.eni)}</span>{" "}
                  <span className="tabular-nums">
                    {t("(score {score}, {percent} % des mots-clés)", {
                      score: doc.score.toLocaleString(locale()),
                      percent: Math.round(doc.confidence * 100),
                    })}
                  </span>
                  .
                </>
              ) : (
                <>{t("aucun ennemi ne ressort nettement : choisissez celui que le compte rendu décrit.")}</>
              )}
              {analysis.truncated && ` ${t("Document long : seul son début a été lu.")}`}
            </p>
            <Segmented
              label={t("Verser au RETEX de")}
              value={eni}
              onChange={setEni}
              options={retexEnemies(custom).map((e) => {
                const name = custom && e === custom.key ? custom.code : e.toUpperCase();
                return { value: e, label: name, title: name };
              })}
            />
            <p className="text-meta leading-snug text-ink-muted">
              {analysis.played === eni
                ? retex.feeds_briefing
                  ? t("Ennemi joué par le dossier : ce RETEX complétera la situation géopolitique de la mise en ambiance.")
                  : t("En contre-rébellion, la situation géopolitique décrit le groupe armé : ce RETEX n'y servira pas.")
                : analysis.played
                  ? t("Le dossier joue l'{played} : ce RETEX servira quand il jouera l'{eni}.", {
                      played: label(analysis.played),
                      eni: label(eni),
                    })
                  : t("Le dossier n'a pas encore d'ennemi : ce RETEX servira quand il jouera cet ennemi.")}
              {dropped > 0 &&
                ` ${t("Le RETEX de l'{eni} en compte déjà {count} : {max} au plus, {fate}.", {
                  eni: label(eni),
                  count: group?.count,
                  max: retex.limits.entries,
                  fate: dropped > 1 ? t("les {n} plus anciens partiront", { n: dropped }) : t("le plus ancien partira"),
                })}`}
            </p>
          </div>
          {analysis.passages.length === 0 ? (
            <p className="px-4 py-8 text-center text-ui text-ink-muted">{t("Aucun passage exploitable dans ce document.")}</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-x-3 px-4 pt-3 pb-1 text-meta text-ink-muted">
                <span>
                  {plural(analysis.passages.length, t("passage"), t("passages"))} · {plural(chosen.size, t("choisi"), t("choisis"))}
                </span>
                <span>
                  {t("Cochés d'office : score {threshold} ou plus pour l'{eni}", {
                    threshold: analysis.threshold,
                    eni: label(eni),
                  })}
                </span>
              </div>
              <ul ref={passagesRef} className="divide-y divide-line">
                {analysis.passages.map((p, i) => (
                  <PassageRow
                    key={i}
                    text={p.text}
                    score={p.scores[eni] ?? 0}
                    threshold={analysis.threshold}
                    checked={chosen.has(i)}
                    onToggle={() =>
                      setChosen((c) => {
                        const next = new Set(c);
                        if (next.has(i)) next.delete(i);
                        else next.add(i);
                        return next;
                      })
                    }
                  />
                ))}
              </ul>
            </>
          )}
          <Presence show={Boolean(error)}>{error && <Callout tone="danger" className="mx-4 my-3">{error}</Callout>}</Presence>
        </div>
      )}
    </Dialog>
  );
}
